import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * One polite live region, mounted at app root and never unmounted.
 *
 * A live region inserted into the DOM at the same moment as its text is
 * unreliable across VoiceOver, NVDA and TalkBack — the node has to pre-exist the
 * change. It matters most in a room game, where the screen changes because a
 * frame arrived from a server with no user action to imply it: a blind player
 * would otherwise sit in silence and only discover the round moved on by
 * re-exploring the page.
 */
const AnnounceContext = createContext<(message: string) => void>(() => {});

export function useAnnounce() {
  return useContext(AnnounceContext);
}

export function AnnounceProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState('');
  const last = useRef('');

  const announce = useCallback((text: string) => {
    if (!text) return;
    // Identical text back-to-back is usually a double-render, but the same
    // sentence can also legitimately recur a round later. A zero-width space
    // makes the node's text change without changing what is spoken.
    setMessage(text === last.current ? `${text}\u200B` : text);
    last.current = text;
  }, []);

  return (
    <AnnounceContext.Provider value={announce}>
      {children}
      <div className="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
        {message}
      </div>
    </AnnounceContext.Provider>
  );
}

/**
 * Moves focus to the screen's heading whenever `key` changes. Without it, a
 * server-driven screen change leaves focus on an element that no longer exists
 * and it reverts to <body> — a WCAG 2.2 SC 2.4.3 problem, and in practice the
 * thing that makes a screen reader go quiet for the rest of the game.
 */
export function useFocusOnChange<T extends HTMLElement>(key: unknown): React.RefCallback<T> {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    ref.current?.focus();
  }, [key]);
  return useCallback((node: T | null) => {
    ref.current = node;
  }, []);
}

/** A transient message. Errors say what happened and what to do. */
export function Toast({ text, onDone }: { text: string | null; onDone: () => void }) {
  useEffect(() => {
    if (!text) return;
    const id = window.setTimeout(onDone, 4200);
    return () => window.clearTimeout(id);
  }, [text, onDone]);
  if (!text) return null;
  return <div className="toast">{text}</div>;
}

/**
 * The screen frame every view shares: a title bar, a scrolling body, and an
 * action rail pinned to the bottom within the safe area.
 */
export function Screen({
  eyebrow,
  title,
  children,
  actions,
  hot,
}: {
  eyebrow?: ReactNode;
  title: string;
  children: ReactNode;
  actions?: ReactNode;
  hot?: boolean;
}) {
  const heading = useFocusOnChange<HTMLHeadingElement>(title);
  return (
    <div className="screen">
      <div className="bar">
        <span className={hot ? 'eyebrow hot' : 'eyebrow'}>{eyebrow}</span>
      </div>
      <h1 className="screen-title" tabIndex={-1} ref={heading}>
        {title}
      </h1>
      {/*
        `tabIndex={0}` because this scrolls. A region that only scrolls with a
        finger or a wheel is unreachable by keyboard, which is WCAG 2.1.1 — and on
        the rules screen the content below the fold is most of the page.
      */}
      <div className="body-area" tabIndex={0}>
        {children}
      </div>
      {actions ? <div className="actions">{actions}</div> : null}
    </div>
  );
}
